import React, { useState } from 'react';
import { formatPriceUSD, isArrayLength, normalizedStatus } from '../../../util/genericHelpers';
import { getProductPriceDetails } from '../../../util/subscriptionHelpers';
import { H2, Icons, PrimaryButton } from '../../../components';
import { useIntl } from 'react-intl';
import SubscriptionPaymentModal from '../SubscriptionPaymentModal';
import { SubscriptionStatus } from '../../../util/enums';
import ConfirmationModal from './ConfirmationModal';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import { useDispatch, useSelector } from 'react-redux';
import { createResourceLocatorString } from '../../../util/routes';
import { useHistory } from 'react-router-dom';
import {
  getUserSubscriptionPlanId,
  getUserSubscriptionStatus,
  getUserType,
} from '../../../util/userDataExtraction';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import css from './SubscriptionCard.module.css';
import { selectIsAuthenticated } from '../../../ducks/auth.duck';

const FeaturesList = () => {
  const intl = useIntl();

  const features = [
    {
      name: 'List Unlimited Services',
      description:
        'Create detailed listings across our 6 provider categories — reach local landowners, travelers, and event organizers.',
    },
    {
      name: 'Get Seen by Your Target Clients',
      description:
        'Show up in front of users actively looking for land and outdoor space services — marketing your business directly to the right audience.',
    },
    {
      name: 'Secure Bookings Through LandVnV',
      description:
        'Accept booking requests, manage your schedule, and get paid through the platform.',
    },
    {
      name: 'Message Interested Clients Directly',
      description:
        'Connect with hosts, guests, or anyone seeking outdoor services to answer questions, coordinate jobs, and build trust.',
    },
    {
      name: 'Custom Pricing & Scheduling',
      description:
        'Choose hourly, per-job, or flat rates—or offer a “Call for Quote” option if you prefer. Set your service area and control your availability.',
    },
    {
      name: 'Build Your Reputation with Ratings & Reviews',
      description: 'Deliver great service and earn 5-star reviews to attract more future bookings.',
    },
    {
      name: 'Cancel Anytime – No Contracts',
      description: 'Risk-free. First month free, then just $15/month with no long-term commitment.',
    },
  ];

  return (
    <>
      <div className={css.planHead}>
        <h2>{intl.formatMessage({ id: 'SubscriptionCard.FeaturesTitle' })}</h2>
      </div>

      <ul className={css.featuresList}>
        {features.map((feature, index) => (
          <li key={index} className={css.featureItem}>
            <Icons name="checkListIcon" />
            <div className={css.featureContent}>
              <strong className={css.featureName}>{feature.name}</strong>
              <br />
              {feature.description && (
                <span className={css.featureDescription}>{feature.description}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};

const SubscriptionCard = ({ subscriptionPrices, currentUser, location }) => {
  const { subscriptionId } = location?.state || {};
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const [paymentMethodModalState, setPaymentMethodModalState] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const userType = getUserType(currentUser);
  const subscriptionStatus = normalizedStatus(getUserSubscriptionStatus(currentUser));
  const activeStatus = normalizedStatus(SubscriptionStatus.ACTIVE);
  const isSubscriptionActive = subscriptionStatus === activeStatus;
  const activeSubscriptionPlanId = getUserSubscriptionPlanId(currentUser);

  const redirectToHomepage = () => {
    history.push(createResourceLocatorString('LandingPage', routeConfiguration, {}, {}));
  };

  return (
    <div className={css.subscriptionContainer}>
      {isArrayLength(subscriptionPrices) ? (
        subscriptionPrices?.map((plan, index) => {
          const {
            price,
            priceName,
            priceId,
            productMetadata,
            isFree,
            isRecurring,
            features = null,
          } = getProductPriceDetails(plan, isSubscriptionActive) || {};
          const displayPrice = formatPriceUSD(price);
          const renderButtons = (
            <div className={css.buttonContainer}>
              <PrimaryButton
                onClick={() => {
                  if (isAuthenticated) {
                    setPaymentMethodModalState({ priceId, priceName: productMetadata?.type });
                  } else {
                    history.push(
                      createResourceLocatorString('SubscriptionBuyPage', routeConfiguration, {}, {})
                    );
                  }
                }}
                type="button"
                className={css.getStartedButton}
                disabled={activeSubscriptionPlanId === priceId || isFree}
              >
                {intl.formatMessage({ id: 'SubscriptionCard.GetStartedButton' })}
              </PrimaryButton>
            </div>
          );
          return (
            <div key={index} className={css.cardContainer}>
              <div className={css.cardHead}>
                <div className={css.headingContainer}>
                  <div className={css.priceName}>{priceName}</div>
                  {productMetadata?.isPopular ? (
                    <span className={css.popularPlan}>
                      {intl.formatMessage({ id: 'SubscriptionCard.PopularPlanLabel' })}
                    </span>
                  ) : null}
                </div>
                <H2 as="h2" className={css.heading}>
                  {isFree ? (
                    intl.formatMessage({ id: 'SubscriptionCard.freeLabel' })
                  ) : (
                    <>
                      {intl.formatMessage(
                        { id: 'SubscriptionCard.PriceLabel' },
                        { priceValue: displayPrice }
                      )}

                      {/* {isRecurring ? (
                        <sub>{intl.formatMessage({ id: 'SubscriptionCard.PerMonthLabel' })}</sub>
                      ) : null} */}
                    </>
                  )}
                </H2>

                {renderButtons}
              </div>
              <div className={css.planDetails}>
                <FeaturesList />
              </div>
            </div>
          );
        })
      ) : (
        <p>{intl.formatMessage({ id: 'SubscriptionPage.NoPlansMessage' })}</p>
      )}
      <SubscriptionPaymentModal
        isOpen={!!paymentMethodModalState?.priceId}
        setPaymentMethodModalState={setPaymentMethodModalState}
        selectedPlanId={paymentMethodModalState?.priceId}
        selectedPlanName={paymentMethodModalState?.priceName}
        currentUser={currentUser}
        activeSubscriptionPlanId={activeSubscriptionPlanId}
        subscriptionId={subscriptionId}
        userType={userType}
        setIsConfirmationModalOpen={() => {
          setIsConfirmationModalOpen(true);
        }}
      />
      <ConfirmationModal
        onManageDisableScrolling={(componentId, disableScrolling) =>
          dispatch(manageDisableScrolling(componentId, disableScrolling))
        }
        isConfirmationModalOpen={isConfirmationModalOpen}
        setIsConfirmationModalOpen={() => {
          setIsConfirmationModalOpen(false);
        }}
        redirectToHomepage={redirectToHomepage}
        currentUser={currentUser}
      />
    </div>
  );
};

export default SubscriptionCard;
